import * as BackgroundTask from "@/platform/background-task";
import * as TaskManager from "@/platform/task-manager";
import { AppState, type AppStateStatus } from "@/platform/app-state";
import { Platform } from "@/platform/platform";
import { possuiTokenDeSessao } from "./Api";
import { possuiSessaoPrestador } from "./ApiPrestador";
import { drenarDiarias, temDiariaParaEnviar } from "./PrestadorDiaria";
import { SyncService } from "./Sync";

/**
 * ENVIO EM SEGUNDO PLANO — a fila drena mesmo com o app FECHADO.
 *
 * Vale para as duas sessões do aparelho: a fila do colaborador e as diárias do PRESTADOR
 * (relato de 01/10/2026: a diária também só subia quando a pessoa abria a tela).
 *
 * Por que existe (relato de 30/09/2026): o app só enviava com a tela aberta. Quem bate o
 * ponto num Wi-Fi que bloqueia os nossos hosts e só reabre o app no dia seguinte, no
 * mesmo Wi-Fi, nunca enviava — a batida esperava alguém abrir o app em outra rede. Com
 * esta tarefa, o sistema acorda o app de tempos em tempos (Android: WorkManager, só com
 * rede; iOS: BGTaskScheduler, quando o sistema quiser) e o dreno roda sem tela, inclusive
 * com o desvio da batida pelos dados móveis do Android.
 *
 * ⚠️ `defineTask` PRECISA rodar no escopo global, antes do expo-router: quando o Android
 * acorda o app sem tela, o JS sobe pelo `index.js` e as rotas (`app/`) nem carregam. Por
 * isso este arquivo é importado pelo `index.js` da raiz — não mova a definição para um
 * componente.
 *
 * WEB: aqui o equivalente do `index.js` é o `src/main.tsx` (importar este arquivo antes do
 * router) e, no Service Worker, o import que registra o mesmo corpo para os eventos
 * `sync`/`periodicsync` (tag `TAG_SYNC_ENVIO`).
 */
export const TAREFA_ENVIO_PENDENTE = "tirvu-envio-pendente";

/**
 * WEB: tag do Background Sync no Service Worker = o nome da tarefa (o handler `sync` /
 * `periodicsync` do `src/sw.ts` roda o corpo registrado em `defineTask` com esta tag).
 */
export const TAG_SYNC_ENVIO = TAREFA_ENVIO_PENDENTE;

/** WEB: Periodic Background Sync — o mesmo "no mínimo 15 min" da tarefa nativa. */
const INTERVALO_PERIODIC_SYNC_MS = 15 * 60 * 1000;

/**
 * WEB: pede ao Service Worker um envio quando houver rede (Background Sync, só Chromium).
 * O evento `sync` sai UMA vez por registro — por isso também é refeito ao ir para segundo
 * plano (`vigiarIdaParaSegundoPlano`), o momento em que a aba pode ser fechada com
 * pendência. Sem SW registrado ou sem suporte: não faz nada. Nunca lança.
 */
export async function solicitarEnvioEmSegundoPlano(): Promise<void> {
  try {
    const sw = (globalThis as any)?.navigator?.serviceWorker;
    if (!sw || typeof sw.getRegistration !== "function") return;
    // `getRegistration` (e não `ready`): sem SW registrado (dev) o `ready` nunca resolve.
    const reg = await sw.getRegistration();
    if (reg?.sync && typeof reg.sync.register === "function") {
      await reg.sync.register(TAG_SYNC_ENVIO);
    }
  } catch (e) {
    console.warn("[SegundoPlano] Background Sync indisponível:", e);
  }
}

/**
 * WEB: Background Sync (one-off) + Periodic Background Sync (só PWA instalado, com a
 * permissão concedida pelo browser). Idempotente. Nunca lança.
 */
async function registrarSyncNoServiceWorker(): Promise<void> {
  await solicitarEnvioEmSegundoPlano();
  try {
    const nav = (globalThis as any)?.navigator;
    const sw = nav?.serviceWorker;
    if (!sw || typeof sw.getRegistration !== "function") return;
    const reg = await sw.getRegistration();
    const periodic = reg?.periodicSync;
    if (!periodic || typeof periodic.register !== "function") return;
    if (nav?.permissions?.query) {
      const perm = await nav.permissions
        .query({ name: "periodic-background-sync" })
        .catch(() => null);
      if (perm && perm.state !== "granted") return;
    }
    const tags: string[] =
      typeof periodic.getTags === "function" ? await periodic.getTags() : [];
    if (tags.includes(TAG_SYNC_ENVIO)) return;
    await periodic.register(TAG_SYNC_ENVIO, {
      minInterval: INTERVALO_PERIODIC_SYNC_MS,
    });
  } catch (e) {
    console.warn("[SegundoPlano] Periodic Background Sync indisponível:", e);
  }
}

let vigiandoSegundoPlano = false;

/** WEB: a cada ida para segundo plano, renova o pedido de envio ao SW (uma vez por processo). */
function vigiarIdaParaSegundoPlano(): void {
  if (vigiandoSegundoPlano) return;
  vigiandoSegundoPlano = true;
  AppState.addEventListener("change", (estado: AppStateStatus) => {
    if (estado !== "active") void solicitarEnvioEmSegundoPlano();
  });
}

TaskManager.defineTask(TAREFA_ENVIO_PENDENTE, async () => {
  try {
    // Sem sessão não há para quem enviar; sem pendência não gasta rede (leitura local).
    // As duas sessões do aparelho: colaborador (fila do Sync) e PRESTADOR (diárias).
    if (
      (await possuiTokenDeSessao()) &&
      (await SyncService.temEnvioPendente())
    ) {
      await SyncService.processOfflineQueue();
    }
    if ((await possuiSessaoPrestador()) && (await temDiariaParaEnviar())) {
      await drenarDiarias();
    }
    return BackgroundTask.BackgroundTaskResult.Success;
  } catch (e) {
    console.warn("[SegundoPlano] envio pendente: erro:", e);
    return BackgroundTask.BackgroundTaskResult.Failed;
  }
});

/**
 * Registra a tarefa periódica (idempotente). 15 min é o mínimo que o Android aceita; o
 * sistema trata como "no mínimo", nunca exato. Falha aqui nunca derruba o app — sem a
 * tarefa, valem os gatilhos com o app aberto (services/SyncAgendador.ts).
 */
export async function registrarEnvioEmSegundoPlano(): Promise<void> {
  // WEB: no RN o web saía aqui (`if (Platform.OS === "web") return;`). No browser a tarefa
  // segue pelo shim (o `defineTask` acima fica numa tabela que o Service Worker reutiliza) e,
  // onde houver suporte (Chromium), pelo Background Sync do SW — ver
  // `registrarSyncNoServiceWorker`. iOS/Safari/Firefox: só com o app aberto (SyncAgendador).
  if (Platform.OS === "web") {
    void registrarSyncNoServiceWorker();
    vigiarIdaParaSegundoPlano();
  }
  try {
    const status = await BackgroundTask.getStatusAsync();
    if (status === BackgroundTask.BackgroundTaskStatus.Restricted) {
      console.warn(
        "[SegundoPlano] tarefas em segundo plano indisponíveis neste aparelho (restritas pelo sistema).",
      );
      return;
    }
    if (await TaskManager.isTaskRegisteredAsync(TAREFA_ENVIO_PENDENTE)) return;
    await BackgroundTask.registerTaskAsync(TAREFA_ENVIO_PENDENTE, {
      minimumInterval: 15,
    });
  } catch (e) {
    console.warn("[SegundoPlano] não foi possível registrar a tarefa:", e);
  }
}
